/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_engine

import io.peekandpoke.klang.audio_bridge.KlangPattern
import io.peekandpoke.klang.audio_bridge.KlangPlaybackSignal
import io.peekandpoke.klang.audio_bridge.infra.KlangCommLink
import io.peekandpoke.ultra.streams.Stream
import io.peekandpoke.ultra.streams.StreamSource

/**
 * Continuous playback that runs indefinitely until explicitly stopped.
 * This is the default playback mode for live coding.
 */
internal class ContinuousPlayback internal constructor(
    override val playbackId: String,
    pattern: KlangPattern,
    context: KlangPlaybackContext,
    onStarted: () -> Unit = {},
    onStopped: () -> Unit = {},
) : KlangCyclicPlayback {

    private val _signals = StreamSource<KlangPlaybackSignal>(KlangPlaybackSignal.Idle)

    override val signals: Stream<KlangPlaybackSignal> = _signals.readonly

    /** Inline-DSL bookkeeping belongs to the playback; the scheduler just uses it. */
    private val registrar = context.registrarFor(playbackId)

    private val controller = KlangPatternScheduler(
        playbackId = playbackId,
        pattern = pattern,
        context = context,
        signals = _signals,
        registrar = registrar,
        onStarted = onStarted,
        onStopped = onStopped,
    )

    override fun updatePattern(pattern: KlangPattern) {
        controller.updatePattern(pattern)
    }

    override fun updateRpm(rpm: Double) {
        controller.updateRpm(rpm)
    }

    override fun reemitVoiceSignals() {
        controller.reemitVoiceSignals()
    }

    override fun heardSec(): Double? = controller.heardSec()

    override fun setGain(gain: Double, rampSec: Double) {
        controller.setGain(gain = gain, rampSec = rampSec)
    }

    override val gain: Double get() = controller.gain

    override fun start() {
        start(KlangCyclicPlayback.Options())
    }

    override fun start(options: KlangCyclicPlayback.Options) {
        controller.start(options)
    }

    override fun stop() {
        controller.stop()
        _signals.removeAllSubscriptions()
    }

    override fun handleFeedback(feedback: KlangCommLink.Feedback) {
        controller.handleFeedback(feedback)
    }
}
